import { PostgrestClient } from "@supabase/postgrest-js";
import {
  type Database,
  type DatabaseWithOptions,
} from "../../../__definitions__/database-definitions";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import { type INTERVAL, type ISO8601_DATE, toISO8601_DATE } from "../../../utils/iso8601";
import { SupabaseEventService } from "../server/supabase-event-service";

type CustomRepeatPeriod = "day" | "week" | "month" | "year";

export type CustomRepeatEnd =
  | {
      type: "never";
    }
  | {
      type: "onDate";
      date: Date;
    }
  | {
      type: "after";
      occurrences: number;
    };

export type CustomRepeatType = {
  every?: number;
  period?: CustomRepeatPeriod;
  end?: CustomRepeatEnd;
};

export function useNumberOfRepeats(
  { period, every, end }: CustomRepeatType,
  startDate?: ISO8601_DATE,
): UseSupabaseLoaderState<
  { number_of_repeats: number; final_occurrence: ISO8601_DATE } | undefined
> {
  return useSupabaseLoader(
    async ({ supabase }) => {
      if (!startDate || !period || !every || !end) return undefined;

      const number_of_repeats = await getNumberOfRepeats(
        { period, every, end },
        startDate,
        supabase,
      );

      if (number_of_repeats === undefined) return undefined;

      const final_occurrence = await new SupabaseEventService(supabase).getFinalOccurrence(
        startDate,
        `${every} ${period}` as INTERVAL,
        number_of_repeats,
      );

      return { number_of_repeats, final_occurrence };
    },
    [period, every, end, startDate],
  );
}

export function useFinalOccurrence(
  startDate?: ISO8601_DATE,
  number_of_repeats?: number,
  interval?: INTERVAL | null,
): UseSupabaseLoaderState<ISO8601_DATE | undefined> {
  return useSupabaseLoader(
    async ({ supabase }) => {
      if (!interval || !number_of_repeats || number_of_repeats === 0 || !startDate)
        return undefined;

      return await new SupabaseEventService(supabase).getFinalOccurrence(
        startDate,
        interval,
        number_of_repeats,
      );
    },
    [startDate, interval, number_of_repeats],
  );
}

async function getNumberOfRepeats(
  repeat: Required<CustomRepeatType>,
  startDate: ISO8601_DATE,
  supabase: PostgrestClient<Database, DatabaseWithOptions["options"], "public">,
): Promise<number | undefined> {
  if (repeat.end.type === "never") return 100;

  if (repeat.end.type === "after") return repeat.end.occurrences;

  if (repeat.end.type === "onDate") {
    const endDate = toISO8601_DATE(repeat.end.date);
    if (!endDate) return undefined;

    return await new SupabaseEventService(supabase).getNumberOfRepeats(
      startDate,
      endDate,
      repeat.period,
      repeat.every,
    );
  }
}
